import React, { useState } from 'react';
import { Dumbbell, BarChart3, BrainCircuit, FileDown, Moon, Sun, BookOpen, Home, LogOut, FolderOpen, User, Users, Database, ClipboardList, X } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { CalculatorIcon, ScaleIcon } from './icons';
import type { AppTab } from '../types';
import { usePlanStore } from '../store/usePlanStore';
import { useAuth } from '../store/useAuthStore';
import { authClient } from '../lib/authClient';
import { useThemeStore } from '../store/useThemeStore';
import { calculateVolumeMetrics } from '../logic/volumeEngine';
import { exportPlanToPDF } from '../utils/pdfExport';
import type { PdfExportOptions } from '../utils/pdfExport';
import PdfExportModal from './PdfExportModal';
import { toast } from 'sonner';

/** Inline hamburger menu icon (lucide-react v0.x doesn't export Menu) */
function MenuIcon({ size = 20 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

const tabs: { id: AppTab; label: string; icon: React.ReactNode }[] = [
  { id: 'builder', label: 'Konstruktor', icon: <Dumbbell size={18} /> },
  { id: 'analytics', label: 'Analityka', icon: <BarChart3 size={18} /> },
  { id: 'coach', label: 'Trener AI', icon: <BrainCircuit size={18} /> },
  { id: 'legend', label: 'Legenda', icon: <BookOpen size={18} /> },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { plan, activeTab, setActiveTab, goToDashboard } = usePlanStore();
  const { user } = useAuth();
  const { dark, toggle } = useThemeStore();
  const [showExportModal, setShowExportModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await authClient.signOut();
      toast.success('Wylogowano pomyślnie');
    } catch (error) {
      toast.error('Błąd podczas wylogowania');
    }
  };

  const handleExport = (options: PdfExportOptions) => {
    if (!plan) return;
    const volPerWeek = plan.weeks.map((w) => calculateVolumeMetrics(w, plan.trainingLevel));
    exportPlanToPDF(plan, volPerWeek, options);
  };

  const navAction = (action: () => void) => {
    action();
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      {/* ── Top Bar ────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-6 flex items-center h-14 gap-2 sm:gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2 select-none flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
              <Dumbbell size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight hidden sm:block">
              APEX<span className="text-brand-500 ml-0.5">Builder</span>
            </span>
          </div>

          {/* ── Desktop Navigation (hidden on mobile) ── */}
          <div className="hidden md:flex items-center gap-1 flex-shrink-0">
            {/* Home button */}
            <button
              onClick={goToDashboard}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === 'dashboard'
                ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              title="Pulpit - lista planów"
            >
              <FolderOpen size={16} />
              <span>Pulpit</span>
            </button>

            {/* Exercise database */}
            <button
              onClick={() => setActiveTab('exercises')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === 'exercises'
                ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              title="Baza ćwiczeń"
            >
              <Database size={16} />
              <span>Baza ćwiczeń</span>
            </button>

            {/* Survey (trainers only) */}
            {user && (user as any).role === 'trainer' && (
              <button
                onClick={() => setActiveTab('survey')}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === 'survey'
                  ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                title="Ankiety dla klientów"
              >
                <ClipboardList size={16} />
                <span>Ankiety</span>
              </button>
            )}

            {/* Calculators */}
            <button
              onClick={() => setActiveTab('calculators')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === 'calculators'
                ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              title="Kalkulatory fitness"
            >
              <CalculatorIcon size={16} />
              <span>Kalkulatory</span>
            </button>

            {/* Plan Comparison */}
            <button
              onClick={() => setActiveTab('comparison')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === 'comparison'
                ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              title="Porównanie planów"
            >
              <ScaleIcon size={16} />
              <span>Porównanie</span>
            </button>
          </div>

          {/* Plan name (if a plan is open) */}
          {plan && (
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400 truncate max-w-[120px] sm:max-w-[200px] hidden sm:block">
              {plan.name}
            </span>
          )}

          {/* ── Desktop Plan Tabs / Legend (hidden on mobile) ── */}
          {plan ? (
            <nav className="hidden md:flex items-center gap-1 ml-auto mr-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                    ${activeTab === tab.id
                      ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }
                  `}
                >
                  {tab.icon}
                  <span className="hidden lg:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          ) : (
            <nav className="hidden md:flex items-center gap-1 ml-auto mr-2">
              <button
                onClick={() => setActiveTab(activeTab === 'legend' ? 'dashboard' : 'legend')}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                  ${activeTab === 'legend'
                    ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }
                `}
              >
                <BookOpen size={18} />
                <span>Legenda</span>
              </button>
            </nav>
          )}

          {/* ── Right-side Actions ── */}
          <div className={`flex items-center gap-1 sm:gap-2 ${!plan ? 'ml-auto' : 'md:ml-0 ml-auto'}`}>
            {/* Export (desktop) */}
            {plan && (
              <button
                onClick={() => setShowExportModal(true)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
              >
                <FileDown size={18} />
                <span className="hidden lg:inline">Eksportuj PDF</span>
              </button>
            )}

            {/* Profile badge (large screens only) */}
            {user && (
              <span className="hidden xl:flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                <User size={12} />
                {user.name}
              </span>
            )}

            {/* Notification bell */}
            <NotificationBell />

            {/* Theme toggle */}
            <button
              onClick={toggle}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
              aria-label="Toggle theme"
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Logout (desktop) */}
            <button
              onClick={handleLogout}
              className="hidden md:block p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-all"
              title="Wyloguj"
            >
              <LogOut size={16} />
            </button>

            {/* ── Mobile hamburger ── */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>

        {/* ── Mobile Menu Dropdown ── */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 animate-fade-in">
            <div className="max-w-[1440px] mx-auto px-3 py-3 space-y-1">

              {/* Main nav */}
              <button
                onClick={() => navAction(goToDashboard)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === 'dashboard' ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
              >
                <FolderOpen size={18} /> Pulpit
              </button>

              <button
                onClick={() => navAction(() => setActiveTab('exercises'))}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === 'exercises' ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
              >
                <Database size={18} /> Baza ćwiczeń
              </button>

              {user && (user as any).role === 'trainer' && (
                <button
                  onClick={() => navAction(() => setActiveTab('survey'))}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === 'survey' ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                >
                  <ClipboardList size={18} /> Ankiety
                </button>
              )}

              <button
                onClick={() => navAction(() => setActiveTab('calculators'))}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === 'calculators' ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
              >
                <CalculatorIcon size={18} /> Kalkulatory
              </button>

              <button
                onClick={() => navAction(() => setActiveTab('comparison'))}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === 'comparison' ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
              >
                <ScaleIcon size={18} /> Porównanie planów
              </button>

              {/* Plan Tabs (when a plan is open) */}
              {plan && (
                <>
                  <div className="border-t border-slate-100 dark:border-slate-800 my-2" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-1">
                    {plan.name}
                  </p>
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => navAction(() => setActiveTab(tab.id))}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === tab.id ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}

                  {/* Export PDF (mobile) */}
                  <button
                    onClick={() => navAction(() => setShowExportModal(true))}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                  >
                    <FileDown size={18} /> Eksportuj PDF
                  </button>
                </>
              )}

              {/* Legend (when no plan open) */}
              {!plan && (
                <button
                  onClick={() => navAction(() => setActiveTab(activeTab === 'legend' ? 'dashboard' : 'legend'))}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === 'legend' ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                >
                  <BookOpen size={18} /> Legenda
                </button>
              )}

              {/* Separator + Logout */}
              <div className="border-t border-slate-100 dark:border-slate-800 my-2" />

              {user && (
                <div className="flex items-center gap-2 px-3 py-1 text-xs text-slate-400">
                  <User size={12} />
                  <span>{user.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-100 dark:bg-brand-950 text-brand-600 dark:text-brand-400 font-semibold ml-auto">
                    {(user as any).role === 'trainer' ? 'Trener' : 'Klient'}
                  </span>
                </div>
              )}

              <button
                onClick={() => navAction(handleLogout)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition"
              >
                <LogOut size={18} /> Wyloguj się
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ── Content ────────────────────────── */}
      <main className="max-w-[1440px] mx-auto px-3 sm:px-6 py-4 sm:py-6">{children}</main>

      {/* ── PDF Export Modal ─────────────── */}
      <PdfExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
      />
    </div>
  );
}
